import { NavigationPath } from '@/router/navigation-path';

const TIME_UNITS = {
	SECOND: 'second',
	MINUTE: 'minute',
	HOUR: 'hour',
	DAY: 'day',
	MONTH: 'month',
	YEAR: 'year'
} as const;

type TimeUnit = (typeof TIME_UNITS)[keyof typeof TIME_UNITS];

interface TimeConfig {
	unit: TimeUnit;
	threshold: number;
	format: string;
}

export const TIME_CONFIGS: TimeConfig[] = [
	{ unit: TIME_UNITS.YEAR, threshold: 31536000, format: 'yıl önce' },
	{ unit: TIME_UNITS.MONTH, threshold: 2592000, format: 'ay önce' },
	{ unit: TIME_UNITS.DAY, threshold: 86400, format: 'gün önce' },
	{ unit: TIME_UNITS.HOUR, threshold: 3600, format: 'saat önce' },
	{ unit: TIME_UNITS.MINUTE, threshold: 60, format: 'dakika önce' },
	{ unit: TIME_UNITS.SECOND, threshold: 0, format: 'saniye önce' }
];

export const LanguageCodes = {
	TR: 'tr-TR'
};

export const SERVICE_RESULT = {
	SUCCESS: 'Success',
	ERROR: 'Error'
};

export const ENVs = {
	asseturl: process.env.STATIC_CDN
};

export const PRIVATE_ROUTES: string[] = [NavigationPath.dashboard];

export const DEVELOPMENT_MODE: boolean = process.env.MODE === 'dev';

export const APPS = {
	COACH: 'COACH',
	GOT_IT: 'GOT_IT',
	SOLVER: 'SOLVER'
};

export const UserType = {
	COACH: 'COACH',
	INSTRUCTOR: 'INSTRUCTOR',
	UNIVERSITY_STUDENT: 'UNIVERSITY_STUDENT'
};

export const APP_TYPES = {
	COACH: 'COACH',
	GOT_IT: 'GOT_IT'
};

export const CONTRACTS = {
	COMMERCIAL_CONFIRM_TEXT: 'COMMERCIAL_CONFIRM_TEXT',
	CLARIFICATION_TEXT: 'CLARIFICATION_TEXT',
	APPLICANT_MEMBERSHIP_AGREEMENT: 'MEMBERSHIP_AGREEMENT'
};

export const SERVICES_LIST = {
	1: 'SOLVER',
	2: 'GOT_IT',
	3: 'COACHING_INSTANT',
	4: 'COACHING',
	5: 'COACHING',
	6: 'GOT_IT_APPOINTMENT'
};

export const SERVICES_COACHING_LIST = {
	4: 'COACHING',
	5: 'COACHING'
};

export const SERVICES_GOT_IT_LECTURE_LIST = {
	6: 'GOT_IT'
};

export const KY_INSTANT_ID = 3;

export const SERVICES_DETAIL_LIST = {
	1: 'SOLVER',
	2: 'GOT_IT',
	3: 'COACHING',
	4: 'COACHING_APPOINTMENTS'
};

export const SERVICES_DETAIL_LIST_FOR_COACHING = {
	COACHING: 3,
	COACHING_APPOINTMENTS: 4
};

export const SERVICES_DETAIL_LIST_FOR_GOT_IT = {
	GOT_IT: 2,
	GOT_IT_APPOINTMENTS: 6,
	GOT_IT_LECTURES: 7
};

export const INTERACTION_STATUS = {
	MEETING_ENDED: 'Görüşme Gerçekleşti!',
	MEETING_ENDED_SUCCESSFULLY: 'Görüşme Gerçekleşti!',
	MEETING_ENDED_WITH_CONNECTION_PROBLEMS: 'Gerçekleşti - Bağlantı Sorunu',
	MEETING_FAILED_WITH_CONNECTION_PROBLEMS: 'Gerçekleşmedi - Bağlantı Sorunu',
	MEETING_FAILED: 'Görüşme Gerçekleşmedi - Katılmadın.',
	CREDIT_REFUND: 'Kredi iade edildi.',
	QUESTION_SOLVED: 'Çözüldü',
	QUESTION_REFUNDED: 'İade edildi'
};

export const INTERACTION_STATUS_ICON = {
	MEETING_ENDED: 'icons-check-circle',
	MEETING_ENDED_SUCCESSFULLY: 'icons-check-circle',
	MEETING_ENDED_WITH_CONNECTION_PROBLEMS: 'icons-check-circle',
	MEETING_FAILED_WITH_CONNECTION_PROBLEMS: 'icons-danger-circle',
	MEETING_FAILED: 'icons-danger-circle',
	CREDIT_REFUND: 'icons-danger-circle'
};

export const REPORT_STATUS = {
	COMPLETED: 'COMPLETED',
	INCOMPLETE: 'INCOMPLETE',
	ABSENT: 'ABSENT'
};

export const INSTANT_MEETING_TYPE = {
	COMMENT_TO_STUDENT: 'commentToStudent',
	COMMENT_TO_PARENT: 'commentToParent',
	COMMENT_TO_BACKOFFICE: 'commentToBackoffice'
};

export const INSTANT_MEETING_TYPE_TEXT = {
	[INSTANT_MEETING_TYPE.COMMENT_TO_STUDENT]: 'Öğrenci Özeti',
	[INSTANT_MEETING_TYPE.COMMENT_TO_PARENT]: 'Veli Özeti',
	[INSTANT_MEETING_TYPE.COMMENT_TO_BACKOFFICE]: 'Operasyon Özeti'
};

export const GOT_IT_MEETING_TYPE = {
	COMMENT_TO_OPERATION: 'commentToOperation'
};

export const GOT_IT_MEETING_TYPE_TEXT = {
	[GOT_IT_MEETING_TYPE.COMMENT_TO_OPERATION]: 'Operasyon Özeti'
};

export const SOLVE_TYPE = {
	WHITE_BOARD: 'WHITE_BOARD',
	CAMERA: 'CAMERA'
} as const;

export const PARTICIPANT_TYPE = {
	STUDENT: 'STUDENT',
	EXPERT: 'EXPERT'
} as const;

export const OK_STATUS_CODE = 200;

export const endDateTimezone = 'YYYY-MM-DDT23:59:59.000+03:00';

export const INPUT_MAX_LENGTH = {
	FEEDBACK: 2500,
	EXPERT_MESSAGE: 2500
};

export const PAYMENT_TYPES = {
	NOT_RATED: 'NOT_RATED',
	PARTIAL_PAYMENT: 'PARTIAL_PAYMENT',
	NO_PAYMENT: 'NO_PAYMENT',
	FULL_PAYMENT: 'FULL_PAYMENT'
};

export const LIVE_SERVICE_TYPES = {
	COACHING: 'COACHING',
	GOT_IT: 'GOT_IT',
	SOLVER: 'SOLVER'
};

export const EXPERT_ATTRIBUTE_EXTERNAL_CODES = {
	BUTIK: 'BUTIK'
};

export const MEETING_TYPES = {
	INSTANT: 'INSTANT',
	APPOINTMENT: 'APPOINTMENT'
};

export const MEETING_SERVICE_TYPES = {
	LECTURE: 'Şimdi Anladım Anlık',
	GUIDANCE: 'Koçum Yanımda Anlık',
	ONE_ON_ONE_COACHING: 'Koçum Yanımda Randevulu',
	ONE_ON_ONE_LECTURE: 'Şimdi Anladım Randevulu'
};

export const Appearance = {
	LIGHT: 'light',
	DARK: 'dark'
} as const;

export type AppearanceType = (typeof Appearance)[keyof typeof Appearance];

export const THEME_ATTRIBUTE = 'data-theme';

export const LOGOUT_DIALOG = {
	TITLE: 'Çıkış yapmak istiyor musunuz?',
	DESCRIPTION: 'Hesabınızdan çıkış yapılacak. Devam etmek için tekrar giriş yapmanız gerekir.',
	CONFIRM_LABEL: 'Çıkış Yap',
	CANCEL_LABEL: 'Vazgeç'
} as const;
